import { GoogleGenAI, Type, GenerateContentResponse } from "@google/genai";
import type { GeneratedFile, StructuredPrSummary } from '../types.ts';
import { logError } from './telemetryService.ts';

const API_KEY = process.env.API_KEY || process.env.GEMINI_API_KEY;

if (!API_KEY) {
  throw new Error("Gemini API key not found. Please set the GEMINI_API_KEY environment variable.");
}
const ai = new GoogleGenAI({ apiKey: API_KEY });

// --- Unified AI Helpers ---

async function* streamContent(prompt: string | { parts: any[] }, systemInstruction: string, temperature = 0.5) {
    try {
        const response = await ai.models.generateContentStream({
            model: 'gemini-2.5-flash',
            contents: prompt as any, // Cast to any to handle both string and parts object
            config: { systemInstruction, temperature }
        });

        for await (const chunk of response) {
            yield chunk.text;
        }
    } catch (error) {
        console.error("Error streaming from AI model:", error);
        logError(error as Error, { prompt, systemInstruction });
        if (error instanceof Error) {
            yield `An error occurred while communicating with the AI model: ${error.message}`;
        } else {
            yield "An unknown error occurred while generating the response.";
        }
    }
}

async function generateJson<T>(prompt: string, systemInstruction: string, schema: any, temperature = 0.2): Promise<T> {
    try {
        const response = await ai.models.generateContent({
            model: "gemini-2.5-flash",
            contents: prompt,
            config: {
                systemInstruction,
                responseMimeType: "application/json",
                responseSchema: schema,
                temperature,
            }
        });
        return JSON.parse(response.text.trim());
    } catch (error) {
        console.error("Error generating JSON from AI model:", error);
        logError(error as Error, { prompt, systemInstruction });
        throw error;
    }
}


// --- Unified Feature Functions ---

export const explainCodeStream = (code: string) => streamContent(
    `Please explain the following code snippet:\n\n\`\`\`\n${code}\n\`\`\``,
    "You are an expert software engineer..."
);

export const generateRegExStream = (description: string) => streamContent(
    `Generate a regex for the following description: "${description}"`,
    "You are an expert in regular expressions... Just the regex literal.",
    0.7
);

export const generateCommitMessageStream = (diff: string) => streamContent(
    `Generate a commit message for the following context of new files being added:\n\n${diff}`,
    "You are an expert programmer who writes excellent, conventional commit messages...",
    0.8
);

export const generateUnitTestsStream = (code: string) => streamContent(
    `Generate Vitest unit tests for this React component code:\n\n\`\`\`tsx\n${code}\n\`\`\``,
    "You are a software quality engineer specializing in writing unit tests...",
    0.6
);

export const formatCodeStream = (code: string) => streamContent(
    `Format this code:\n\n\`\`\`javascript\n${code}\n\`\`\``,
    "You are a code formatter... Respond with only the formatted code, enclosed in a markdown block.",
    0.2
);

export const generateComponentFromImageStream = (base64Image: string) => streamContent(
    {
        parts: [
            { text: "Generate a React component based on this image." },
            { inlineData: { mimeType: 'image/png', data: base64Image } }
        ]
    },
    "You are an expert frontend developer..."
);

export const transcribeAudioToCodeStream = (base64Audio: string, mimeType: string) => streamContent(
    {
        parts: [
            { text: "Generate code based on my speech." },
            { inlineData: { mimeType, data: base64Audio } }
        ]
    },
    "You are an expert programmer..."
);

export const transferCodeStyleStream = (args: { code: string, styleGuide: string }) => streamContent(
    `Rewrite the following code to match the provided style guide.\n\nStyle Guide:\n${args.styleGuide}\n\nCode to rewrite:\n\`\`\`\n${args.code}\n\`\`\``,
    "You are an AI assistant that rewrites code to match a specific style guide...",
    0.3
);

export const generateCodingChallengeStream = (_: any) => streamContent(
    `Generate a new coding challenge.`,
    "You are an AI that creates unique and interesting coding challenges...",
    0.9
);

export const reviewCodeStream = (code: string) => streamContent(
    `Please review the following code:\n\n\`\`\`\n${code}\n\`\`\``,
    "You are a senior software engineer performing a code review...",
    0.6
);

export const generateChangelogFromLogStream = (log: string) => streamContent(
    `Analyze this git log and create a changelog:\n\n\`\`\`\n${log}\n\`\`\``,
    "You are a git expert and project manager. Analyze the provided git log and generate a clean, categorized changelog in Markdown format. Group changes under 'Features' and 'Fixes'.",
    0.6
);


export const enhanceSnippetStream = (code: string) => streamContent(
    `Enhance this code snippet:\n\n\`\`\`\n${code}\n\`\`\``,
    "You are a senior software engineer... Respond with only the enhanced code in a markdown block.",
    0.5
);

export const summarizeNotesStream = (notes: string) => streamContent(
    `Summarize these notes:\n\n${notes}`,
    "You are a productivity assistant...",
    0.7
);

export const migrateCodeStream = (code: string, from: string, to: string) => streamContent(
    `Translate this ${from} code to ${to}:\n\n\`\`\`\n${code}\n\`\`\``,
    `You are an expert polyglot programmer who specializes in migrating code between languages and frameworks...`,
    0.4
);

export const analyzeConcurrencyStream = (code: string) => streamContent(
    `Analyze this code for concurrency issues:\n\n\`\`\`javascript\n${code}\n\`\`\``,
    "You are an expert in JavaScript concurrency and web workers...",
    0.6
);

// --- STRUCTURED JSON ---

export interface StructuredExplanation {
    summary: string;
    lineByLine: { lines: string; explanation: string }[];
    complexity: { time: string; space: string };
    suggestions: string[];
}
export const explainCodeStructured = async (code: string): Promise<StructuredExplanation> => {
    const systemInstruction = "You are an expert software engineer providing a structured analysis of a code snippet.";
    const prompt = `Analyze this code: \n\n\`\`\`\n${code}\n\`\`\``;
    const schema = {
        type: Type.OBJECT,
        properties: {
            summary: { type: Type.STRING, description: "A high-level summary of what the code does." },
            lineByLine: {
                type: Type.ARRAY,
                description: "A line-by-line or block-by-block explanation.",
                items: {
                    type: Type.OBJECT,
                    properties: {
                        lines: { type: Type.STRING, description: "The line range, e.g., '1-5'." },
                        explanation: { type: Type.STRING, description: "The explanation for that line range." }
                    },
                    required: ["lines", "explanation"]
                }
            },
            complexity: {
                type: Type.OBJECT,
                description: "Big O notation for time and space complexity.",
                properties: {
                    time: { type: Type.STRING, description: "e.g., O(n^2)" },
                    space: { type: Type.STRING, description: "e.g., O(1)" }
                },
                required: ["time", "space"]
            },
            suggestions: {
                type: Type.ARRAY,
                description: "Suggestions for improvement or alternatives.",
                items: { type: Type.STRING }
            }
        },
        required: ["summary", "lineByLine", "complexity", "suggestions"]
    };

    return generateJson(prompt, systemInstruction, schema);
}

export interface Theme {
    primary: string;
    background: string;
    surface: string;
    textPrimary: string;
    textSecondary: string;
}
export const generateThemeFromDescription = async (description: string): Promise<Theme> => {
    const systemInstruction = "You are a UI/UX design expert specializing in color theory. Generate a color theme based on the user's description. Provide hex codes for each color.";
    const prompt = `Generate a color theme for: "${description}"`;
    const schema = {
        type: Type.OBJECT,
        properties: {
            primary: { type: Type.STRING, description: "The primary accent color." },
            background: { type: Type.STRING, description: "The main background color." },
            surface: { type: Type.STRING, description: "The color for card backgrounds or surfaces." },
            textPrimary: { type: Type.STRING, description: "The color for primary text." },
            textSecondary: { type: Type.STRING, description: "The color for secondary or muted text." }
        },
        required: ["primary", "background", "surface", "textPrimary", "textSecondary"]
    };
    return generateJson(prompt, systemInstruction, schema);
};

/**
 * Generates an image from a text prompt using a native image generation model.
 * @param prompt A text description of the image to generate.
 * @returns A promise that resolves with the data URL of the generated image.
 */
export const generateImage = async (prompt: string): Promise<string> => {
    try {
        const response = await ai.models.generateImages({
            model: 'imagen-3.0-generate-002',
            prompt: prompt,
            config: {
              numberOfImages: 1,
              outputMimeType: 'image/png',
              aspectRatio: '16:9',
            },
        });
        
        const base64ImageBytes: string | undefined = response.generatedImages[0]?.image?.imageBytes;

        if (base64ImageBytes) {
             return `data:image/png;base64,${base64ImageBytes}`;
        }
        
        throw new Error("AI did not return an image. It may have been blocked or the model was unable to generate one for the given prompt.");
    } catch (error) {
        console.error("Error during native image generation:", error);
        logError(error as Error, { prompt });
        throw new Error(`AI did not return an image. It may have been blocked or the model was unable to generate one for the given prompt.`);
    }
};

// --- Functions below are placeholders for missing implementations inferred from component usage ---
// In a real scenario, these would be fully implemented.

export const generateFeature = async (prompt: string): Promise<GeneratedFile[]> => { console.warn("generateFeature not implemented"); return []; };
export const generateUnitTests = async (code: string): Promise<string> => { console.warn("generateUnitTests not implemented"); return ""; };
export const generateCommitMessage = async (diff: string): Promise<string> => { console.warn("generateCommitMessage not implemented"); return ""; };
export const generatePrSummaryStructured = async (diff: string): Promise<StructuredPrSummary> => { console.warn("generatePrSummaryStructured not implemented"); return {title: "", summary: "", changes: []}; };
export const generateColorPalette = async (color: string): Promise<{colors: string[]}> => { console.warn("generateColorPalette not implemented"); return {colors: []}; };
export interface CronParts { minute: string; hour: string; dayOfMonth: string; month: string; dayOfWeek: string; }
export const generateCronFromDescription = async (prompt: string): Promise<CronParts> => { console.warn("generateCronFromDescription not implemented"); return {minute: '*', hour: '*', dayOfMonth: '*', month: '*', dayOfWeek: '*'}; };
export const getInferenceFunction = async (prompt: string, declarations: any[], knowledge: string): Promise<any> => { console.warn("getInferenceFunction not implemented"); return { text: "This function is not implemented."}; };
